<template>
  <div class="form-group">
        <label for="follow-up-date">
            <slot name="label">{{ label }}</slot><span class="text-danger">*</span>
        </label>
        <div class="follow-up-datetimepicker">
            <flat-pickr ref="datetimepicker" :id="id" class="form-control bg-white flatpicker-calendar-todo" v-model="followUpDate" :config="flatpickrOptions" @input="onInput" />
            <i class="fas fa-calendar-alt" :class="{ 'text-danger': validationError }" @click="openFlatpickr"></i>
        </div>
        <div class="text-danger mt-1" v-if="validationError">
            {{ validationError }}
        </div>
    </div>
</template>

<script>
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';

export default {
    name: 'DatetimePicker',
    components: { FlatPickr },
    props: {
        id: {
            type: String,
            required: false,
            default: 'datetimepicker',
        },
        label: {
            type: String,
            required: false,
            default: 'Date',
        },
        value: {
            type: String,
            required: false,
            default: () => {
                const now = new Date();

                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');

                let hours = now.getHours();
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const ampm = hours >= 12 ? 'PM' : 'AM';

                hours = hours % 12;
                hours = hours ? hours : 12;

                const formattedHours = String(hours).padStart(2, '0');

                return `${year}-${month}-${day} ${formattedHours}:${minutes} ${ampm}`;
            }
        }
    },
    data() {
        return {
            internalValue: this.value,
            followUpDate: this.date ? new Date(this.date) : null,
            validationError: '',
            flatpickrOptions: {
                enableTime: true,
                time_24hr: false,
                minDate: (() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return today;
                })(),
                allowInput: false,
                altInput: true,
                altFormat: 'd/m/Y G:i:S K',
                dateFormat: 'Y-m-d H:i:S',
                closeOnSelect: false,
                onReady: [(selectedDates, dateStr, instance) => {
                    if (instance.altInput) {
                        instance.altInput.placeholder = 'Select';
                    }

                    this.applyCustomYearPicker(instance);
                }],
                onOpen: [(selectedDates, dateStr, instance) => {
                    requestAnimationFrame(() => {
                        const calendar = instance.calendarContainer;
                        const input = instance.altInput;

                        const timeContainer = calendar.querySelector('.flatpickr-time');

                        // Match calendar width to input
                        const inputWidth = input?.getBoundingClientRect().width;
                        calendar.style.width = `${inputWidth}px`;

                        this.applyCustomYearPicker(instance);
                        this.applyCustomDatePicker(instance);

                        // Customize time picker
                        if (timeContainer ) {
                            timeContainer.innerHTML = '';

                            const selectedDate = instance.selectedDates[0];
                            let selectedHour = null;
                            let selectedMinute = null;
                            let selectedAMPM = null;

                            if (selectedDate) {
                                selectedHour = instance.config.time_24hr ? String(selectedDate.getHours()).padStart(2, '0') : String((selectedDate.getHours() % 12) || 12);
                                selectedMinute = String(selectedDate.getMinutes()).padStart(2, '0');
                                selectedAMPM = selectedDate.getHours() >= 12 ? 'PM' : 'AM';
                            }

                            const hours = instance.config.time_24hr ? Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')) : ['12', ...Array.from({ length: 11 }, (_, i) => String(i + 1))];
                            const minutes = Array.from({ length: 6 }, (_, i) => String(i * 10).padStart(2, '0'));

                            const createScroller = (options, onClick, selectedValue) => {
                                const container = document.createElement('div');
                                container.classList.add('hide-scrollbar');
                                Object.assign(container.style, {
                                    overflowY: 'auto',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: '0',
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                });

                                options.forEach(val => {
                                    const opt = document.createElement('div');
                                    opt.textContent = val;
                                    Object.assign(opt.style, {
                                        padding: '2px',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                    });

                                    if (val === selectedValue) opt.classList.add('selected-time');

                                    opt.addEventListener('mouseover', () => {
                                        if (val !== selectedValue) {
                                            opt.style.background = '#6c757d';
                                            opt.style.color = 'white';
                                        }
                                    });
                                    opt.addEventListener('mouseout', () => {
                                        if (val !== selectedValue) {
                                            opt.style.background = 'transparent';
                                            opt.style.color = '#23282c';
                                        }
                                    });
                                    opt.addEventListener('click', () => {
                                        onClick(val);
                                        container.querySelectorAll('div').forEach(el => el.classList.remove('selected-time'));
                                        opt.classList.add('selected-time');
                                    });

                                    container.appendChild(opt);
                                });

                                // Bottom spacer for scroll padding
                                const spacer = document.createElement('div');
                                spacer.style.cssText = 'height: 220px; flex-shrink: 0; pointer-events: none;';
                                container.appendChild(spacer);

                                return container;
                            };

                            let hourScroller, minuteScroller, ampmScroller;

                            const setTime = (unit, value) => {
                                const date = instance.selectedDates[0] || new Date();
                                const newDate = new Date(date);

                                if (unit === 'hour') {
                                    let hour = parseInt(value);

                                    if (!instance.config.time_24hr) {
                                        const isPM = newDate.getHours() >= 12;

                                        if (isPM && hour < 12) {
                                            hour += 12;
                                        }

                                        if (!isPM && hour === 12) {
                                            hour = 0;
                                        }
                                    }
                                    
                                    newDate.setHours(hour);
                                } else if (unit === 'minute') {
                                    newDate.setMinutes(parseInt(value));
                                } else if (unit === 'ampm') {
                                    let hour = newDate.getHours();
                                    
                                    if (value === 'PM' && hour < 12) {
                                        hour += 12;
                                    }
                                    
                                    if (value === 'AM' && hour >= 12) {
                                        hour -= 12;
                                    }

                                    newDate.setHours(hour);
                                }

                                instance.setDate(newDate, true);
                                if (instance.altInput) {
                                    instance.altInput.value = instance.formatDate(newDate, instance.config.altFormat);
                                }

                                highlightSelected(unit === 'hour' ? hourScroller : unit === 'minute' ? minuteScroller : ampmScroller, value);
                            };

                            const highlightSelected = (scroller, value) => {
                                scroller?.querySelectorAll('div').forEach(opt =>
                                    opt.classList.toggle('selected-time', opt.textContent === value)
                                );
                            };

                            hourScroller = createScroller(hours, val => setTime('hour', val), selectedHour);
                            minuteScroller = createScroller(minutes, val => setTime('minute', val), selectedMinute);
                            timeContainer.appendChild(hourScroller);
                            timeContainer.appendChild(minuteScroller);

                            if (!instance.config.time_24hr) {
                                ampmScroller = createScroller(['AM', 'PM'], val => setTime('ampm', val), selectedAMPM);
                                timeContainer.appendChild(ampmScroller);
                            }
                        }
                    });
                }],
                onChange: [(selectedDates, dateStr, instance) => {
                    if (!selectedDates[0]) {
                        return;
                    }

                    const newDate = new Date(selectedDates[0]);
                    
                    if (!this.followUpDate || newDate.getTime() !== new Date(this.followUpDate).getTime()) {
                        this.followUpDate = newDate;
                        if (!isNaN(newDate.getTime())) {
                            this.validationError = '';
                            this.$refs.datetimepicker?.fp?.altInput?.classList.remove('is-invalid');
                        }
                    }
                }],
                onMonthChange: [(selectedDates, dateStr, instance) => {
                    requestAnimationFrame(() => {
                        this.applyCustomYearPicker(instance);
                        this.applyCustomDatePicker(instance);
                        this.disablePreviousDate(instance);
                    });
                }],
                onValueUpdate: [(selectedDates, dateStr, instance) => {
                    requestAnimationFrame(() => {
                        this.applyCustomYearPicker(instance);
                        this.applyCustomDatePicker(instance);
                        this.disablePreviousDate(instance);
                    });
                }],
            }
        }
    },
    watch: {
        value(val) {
            this.internalValue = val
        }
    },
    methods: {
        openFlatpickr() {
            const datetimepicker = this.$refs.datetimepicker

            if (datetimepicker && datetimepicker.fp) {
                datetimepicker.fp.open()
            }
        },
        applyCustomDatePicker(instance) {
            const calendar = instance.calendarContainer;
            const innerContainer = calendar.querySelector('.flatpickr-innerContainer');
            const dateContainer = calendar.querySelector('.flatpickr-rContainer');
            const monthsContainer = calendar.querySelector('.flatpickr-months');
            const daysContainer = calendar.querySelector('.flatpickr-days');
            const dayContainer = calendar.querySelector('.dayContainer');
            const dayItemWrappers = calendar.querySelectorAll('.flatpickr-day');
            const timeContainer = calendar.querySelector('.flatpickr-time');
            const nextMonth = calendar.querySelector('.flatpickr-next-month');
            const prevMonth = calendar.querySelector('.flatpickr-prev-month');

            if (monthsContainer && dateContainer && monthsContainer.parentNode !== dateContainer) {
                dateContainer.insertBefore(monthsContainer, dateContainer.firstChild);
            }

            if (nextMonth) {
                Object.assign(nextMonth.style, {
                    position: 'relative',
                    left: 'auto',
                    right: '0'
                });
            }

            if (prevMonth) {
                Object.assign(prevMonth.style, {
                    left: '0',
                    right: 'auto'
                });
            }

            if (innerContainer) {
                Object.assign(innerContainer.style, {
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                });
            }

            if (dateContainer) {
                Object.assign(dateContainer.style, {
                    flex: '2 1 0',
                    maxWidth: '70%'
                });
            }

            if (daysContainer) {
                Object.assign(daysContainer.style, {
                    width: '100%'
                });
            }

            if (dayContainer) {
                Object.assign(dayContainer.style, {
                    width: '100%',
                    maxWidth: '100%'
                });
            }

            if (timeContainer && dateContainer) {
                requestAnimationFrame(() => {
                    const dateContainerHeight = dateContainer.getBoundingClientRect().height;

                    Object.assign(timeContainer.style, {
                        display: 'flex',
                        flexDirection: 'row',
                        flex: '1 1 0',
                        justifyContent: 'flex-end',
                        gap: '10px',
                        border: 'none',
                        maxWidth: '30%',
                        padding: '8px',
                        height: `${dateContainerHeight}px`,
                        boxSizing: 'border-box',
                        overflowY: 'auto',
                        maxHeight: 'none',
                        marginRight: '-5px'
                    });

                    if (dateContainer && innerContainer && dateContainer.nextSibling !== timeContainer) {
                        innerContainer.insertBefore(timeContainer, dateContainer.nextSibling);
                    }

                    if (monthsContainer && dateContainer && monthsContainer.parentNode !== dateContainer) {
                        dateContainer.insertBefore(monthsContainer, dateContainer.firstChild);
                    }
                });
            }

            dayItemWrappers.forEach(wrapper => {
                Object.assign(wrapper.style, {
                    width: 'calc(100% / 7)',
                    maxWidth: 'none',
                    height: '50px',
                    lineHeight: '50px'
                });
            });
        },
        applyCustomYearPicker(instance) {
            const calendar = instance.calendarContainer;
            if (!calendar) {
                return;
            }

            // Hide default year input container completely
            const defaultYearInputWrapper = calendar.querySelector('.numInputWrapper');
            if (defaultYearInputWrapper) {
                defaultYearInputWrapper.style.display = 'none';
            }

            // Find the current month container (.flatpickr-current-month)
            const currentMonth = calendar.querySelector('.flatpickr-current-month');
            if (!currentMonth) {
                return;
            }

            // Find the month dropdown container (.flatpickr-monthDropdown-months)
            const monthDropdown = currentMonth.querySelector('.flatpickr-monthDropdown-months');
            if (!monthDropdown) {
                return;
            }

            // Check if custom year button already exists, to avoid duplicates
            if (currentMonth.querySelector('.flatpickr-year-button')) {
                return;
            }

            // Create the year button
            const yearBtn = document.createElement('button');
            yearBtn.type = 'button';
            yearBtn.className = 'flatpickr-year-button';
            yearBtn.textContent = instance.currentYear;
            Object.assign(yearBtn.style, {
                background: 'aliceblue',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'smaller',
                marginLeft: '8px',
                padding: '6px 10px',
                borderRadius: '4px',
            });

            // Insert the yearBtn immediately after the monthDropdown
            monthDropdown.insertAdjacentElement('afterend', yearBtn);

            // Show your year dropdown popup
            yearBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.showYearDropdown(instance, yearBtn);
            });
        },
        showYearDropdown(instance, anchorEl) {
            const existingDropdown = instance.calendarContainer.querySelector('.custom-year-dropdown');
            if (existingDropdown) existingDropdown.remove();

            const dropdown = document.createElement('div');
            dropdown.className = 'custom-year-dropdown';

            Object.assign(dropdown.style, {
                position: 'absolute',
                zIndex: 9999,
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                padding: '10px',
                maxHeight: '220px',
                overflowY: 'auto',
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '8px',
            });

            dropdown.setAttribute('tabindex', '-1');
            dropdown.addEventListener('mousedown', e => e.stopPropagation());
            dropdown.addEventListener('click', e => e.stopPropagation());

            const currentYear = new Date().getFullYear();
            const selectedYear = instance.currentYear;

            const startYear = currentYear;
            const endYear = currentYear + 10;

            for (let year = startYear; year <= endYear; year++) {
                const btn = document.createElement('button');
                btn.textContent = year;
                btn.type = 'button';
                Object.assign(btn.style, {
                    padding: '6px',
                    fontSize: '14px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    background: year === selectedYear ? '#2563eb' : 'transparent',
                    color: year === selectedYear ? 'white' : '#333',
                });

                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    // Temporarily prevent closing
                    instance.config.closeOnSelect = false;

                    const selectedDate = instance.selectedDates[0] || new Date();
                    const newDate = new Date(selectedDate);

                    newDate.setFullYear(year);

                    // Set the date (keeps day/month but updates the year)
                    instance.setDate(newDate, false);

                    // Manually update view to reflect new year
                    instance.currentYear = year;
                    instance.currentMonth = newDate.getMonth();
                    instance.redraw();

                    const calendar = instance.calendarContainer;
                    if (calendar) {
                        const yearBtn = calendar.querySelector('.flatpickr-year-button');
                        if (yearBtn) {
                            yearBtn.textContent = year;
                        }
                    }

                    setTimeout(() => {
                        this.limitMonthsByYear(instance, year);
                        this.applyCustomYearPicker(instance);
                        this.applyCustomDatePicker(instance);
                        this.disablePreviousDate(instance);

                        // Restore default closing behavior
                        instance.config.closeOnSelect = true;
                    }, 0);

                    dropdown.remove();
                });

                btn.addEventListener('mouseover', () => {
                    btn.style.background = '#e2e8f0';
                    btn.style.color = '#1e40af';
                });

                btn.addEventListener('mouseout', () => {
                    if (year !== selectedYear) {
                        btn.style.background = 'transparent';
                        btn.style.color = '#333';
                    }
                });

                dropdown.appendChild(btn);
            }

            // Append inside calendarContainer
            instance.calendarContainer.appendChild(dropdown);

            const rect = anchorEl.getBoundingClientRect();
            const calRect = instance.calendarContainer.getBoundingClientRect();

            dropdown.style.top = `${anchorEl.offsetTop + anchorEl.offsetHeight}px`;
            dropdown.style.left = `${anchorEl.offsetLeft}px`;
        },
        limitMonthsByYear(instance, selectedYear) {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth();

            // Enable all months first
            for (let m = 0; m < 12; m++) {
                instance._bindMonthChangeListeners?.();
                instance.set('minDate', null);
            }

            if (selectedYear === currentYear) {
                // Disable previous months
                const minDate = new Date(selectedYear, currentMonth, 1);
                instance.set('minDate', minDate);
            } else {
                instance.set('minDate', null);
            }
        },
        disablePreviousDate(instance) {
            const calendar = instance.calendarContainer;
            if (!calendar) {
                return;
            }

            const currentYear = new Date().getFullYear();
            if (instance.currentYear !== currentYear) {
                return;
            }

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const dayElems = calendar.querySelectorAll('.flatpickr-day');
            dayElems.forEach(day => {
                const date = day.dateObj;
                if (date < today) {
                    day.classList.add('flatpickr-disabled');
                    day.removeAttribute('tabindex');
                }
            });
        },
        onInput(val) {
            this.internalValue = val;
            this.$emit('input', val);
        },
    }
}
</script>
